import pick from 'lodash/pick'
import { FC } from 'react'
import toast from 'react-hot-toast'
import { useShallow } from 'zustand/react/shallow'

import useLoadDashboard from 'src/hooks/useLoadDashboard'
import useProductPage from 'src/hooks/useProductPage'
import useQueryParams from 'src/hooks/useQueryParams'
import useDashboardsService from 'src/services/dashboardService'
import useMenusStore from 'src/stores/useMenusStore'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import { getDefaultDashboardFn } from 'src/utils/dashboardsUtils'
import { findSubProductOnMenusFn, removeDashboardFromMenusFn } from 'src/utils/menuUtils'
import ConfirmModal from '../../../ConfirmModal'
import { usePageHeaderDashboardMenuContext } from '../../PageHeaderDashboardMenuProvider'

const ModalDeleteDashboard: FC = () => {
  const { modalOpened, setModalOpened } = usePageHeaderDashboardMenuContext()
  const { deleteDashboard } = useDashboardsService()
  const { selectedDashboard, setSelectedDashboard } = useSelectedDashboardStore()
  const { menus, setMenus } = useMenusStore(useShallow(s => pick(s, ['setMenus', 'menus'])))
  const { setQueryParams } = useQueryParams()
  const { code, product } = useProductPage()

  const { handleGetDashboard } = useLoadDashboard(code)

  const handleDeleteDashboard = async () => {
    if (!selectedDashboard) {
      return
    }
    setModalOpened(null)
    const subProduct = findSubProductOnMenusFn({
      menus,
      productCode: product,
      subProductCode: code,
    })

    if (!subProduct) {
      return
    }

    const removedDashboard = selectedDashboard

    await toast.promise(
      deleteDashboard({
        id: selectedDashboard.id,
        programId: selectedDashboard.program_id?.toString(),
      }),
      {
        loading: 'Please wait...',
        success: (
          <span>
            <b>{removedDashboard.name}</b> dashboard successfully deleted
          </span>
        ),
        error: 'An error occurred. Please retry.',
      },
      { duration: 4000 },
    )

    const nextDashboard = getDefaultDashboardFn({
      dashboards:
        subProduct.dashboards?.filter(
          d => d.id !== selectedDashboard.id && d.program_id === removedDashboard.program_id,
        ) || [],
    })

    setSelectedDashboard(nextDashboard)

    setQueryParams({ dashboard_id: nextDashboard?.id })

    handleGetDashboard({
      id: nextDashboard?.id?.toString() || null,
      programId: nextDashboard?.program_id,
    })

    setMenus(m => ({ ...m, menus: removeDashboardFromMenusFn(m.menus, removedDashboard.id) }))
  }

  return (
    <ConfirmModal
      title="Delete"
      message="Are you sure you want to delete this dashboard ?"
      isOpen={modalOpened === 'delete'}
      onConfirmClick={handleDeleteDashboard}
      onCancelClick={() => setModalOpened(null)}
    />
  )
}

export default ModalDeleteDashboard
